import * as ngCommonHttp from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DistributionJobs } from '@constants/api';
import {
  DistributionJobRequest,
  DistributionRule,
  PagedResourcesDistributionRule,
} from '@generated/tcea/models';
import { DistributionService } from '@generated/tcea/services/distribution.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AbstractDistributionRulesService } from '@abstract-services/distribution-rules/1.0/distribution-rules.abstract-service';
import { CreateJob$Params } from '@generated/tcea/fn/distribution/create-job';
import { UpdateJob$Params } from '@generated/tcea/fn/distribution/update-job';

@Injectable({
  providedIn: 'root',
})
export class DistributionRulesService extends AbstractDistributionRulesService {
  distributionJobsUrl: string;

  constructor(
    private generatedRuleServiceService: DistributionService,
    private http: ngCommonHttp.HttpClient,
  ) {
    super();
    this.distributionJobsUrl = DistributionJobs.DELETE;
  }

  /**
   * Gets all segments for current brand context
   *
   * @param brandId [string]
   * @param params
   * @returns observable of all stores
   */
  getRules(
    brandId: string,
    params: {
      size?: number;
      page?: number;
      filter?: Array<string>;
      archived?: boolean;
    },
  ): Observable<{ data: DistributionRule[]; pages: number }> {
    const { archived, page, size } = params;

    return this.generatedRuleServiceService
      .getRules({
        'Active-Tenant-Identifier': brandId,
        page,
        archived,
        size: size || 100,
      })
      .pipe(
        map((response: PagedResourcesDistributionRule) => ({
          data: response.content || [],
          pages: response.pageMetadata?.totalPages || 0,
        })),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets rule for the ruleId provided
   *
   * @param brandId [string]
   * @param ruleId [string]
   * @returns observable of all stores
   */
  getRuleById(brandId: string, ruleId: string): Observable<DistributionRule> {
    return this.generatedRuleServiceService
      .getRule({
        'Active-Tenant-Identifier': brandId,
        ruleId,
      })
      .pipe(
        map((rule: DistributionRule) => rule),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Creates new distribution rule
   *
   * @param brandId [string]
   * @param ruleData [object]
   * @returns observable rule created
   */
  create(brandId: string, ruleData: DistributionRule): Observable<DistributionRule> {
    return this.generatedRuleServiceService
      .createRule({
        'Active-Tenant-Identifier': brandId,
        body: ruleData,
      })
      .pipe(
        map((rule: DistributionRule) => rule),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Updates existing distribution rule
   *
   * @param brandId [string]
   * @param ruleId [string]
   * @param ruleData [object]
   * @returns observable rule updated
   */
  update(
    brandId: string,
    ruleId: string,
    ruleData: DistributionRule,
  ): Observable<DistributionRule> {
    return this.generatedRuleServiceService
      .updateRule({
        'Active-Tenant-Identifier': brandId,
        ruleId: ruleId,
        body: ruleData,
      })
      .pipe(
        map((rule: DistributionRule) => rule),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  addJob(
    brandId: string,
    ruleId: string,
    jobData: DistributionJobRequest,
  ): Observable<DistributionRule | DistributionJobRequest> {
    let params: CreateJob$Params = {
      'Active-Tenant-Identifier': brandId,
      ruleId: ruleId,
      body: jobData,
    };

    return this.generatedRuleServiceService.createJob(params).pipe(
      map((rule: DistributionRule) => rule),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  cancelJob(brandId: string, jobId: string): Observable<DistributionJobRequest> {
    const URL = this.distributionJobsUrl.replace(':jobId', jobId);
    return this.http.delete(URL).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  editJob(
    brandId: string,
    jobData: DistributionJobRequest,
  ): Observable<DistributionRule | DistributionJobRequest> {
    let params: UpdateJob$Params = {
      'Active-Tenant-Identifier': brandId,
      jobId: jobData.id,
      body: jobData,
    };

    return this.generatedRuleServiceService.updateJob(params).pipe(
      map((rule: DistributionRule) => rule),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getJobsForRule(brandId: string, ruleId: string): Observable<DistributionJobRequest[]> {
    return this.generatedRuleServiceService
      .getJobsForRule({
        'Active-Tenant-Identifier': brandId,
        ruleId: ruleId,
      })
      .pipe(
        map((jobs: any) => jobs._embedded.distribution),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }
}
